import React, { useEffect, useState, useCallback } from 'react';
import { View, Alert, Linking, Text, Platform } from 'react-native';
import { Button, ActivityIndicator, Portal, Modal } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdminOrSuperAgentRole } from '../redux/slices/authSlice';
import { fetchCommandeById, updateStatutCommande, deleteCommande } from '../redux/slices/commandesSlice';
import { Avatar, Badge, BadgeTone, Card, FilterChip, Screen, SectionHeader, TextInput } from '../components/ui';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { RootStackParamList } from '../navigation/AppNavigator';
import { StatutCommande, ModePaiement, getServiceDisplayName } from '../types';
import api from '../services/api';
import { paiementService } from '../services/paiementService';
import { pdfService } from '../services/pdfService';

type RouteType = RouteProp<RootStackParamList, 'DetailCommande'>;
type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

/** Devis → warning, Bon de commande → brand, Facture / Payée → success. */
const STATUT_TONES: Record<string, BadgeTone> = {
  DEVIS: 'warning',
  PAYEE: 'success',
  FACTURE: 'success',
  BON_COMMANDE: 'brand',
};

const STATUT_LABELS: Record<string, string> = {
  DEVIS: 'Devis',
  PAYEE: 'Payée',
  FACTURE: 'Facturé',
  BON_COMMANDE: 'Bon de commande',
};

const MODE_OPTIONS: { value: ModePaiement; label: string; icon: IconName }[] = [
  { value: 'ESPECES', label: 'Espèces', icon: 'cash' },
  { value: 'VIREMENT', label: 'Virement', icon: 'bank-transfer' },
  { value: 'CHEQUE', label: 'Chèque', icon: 'checkbook' },
];

const ECHEANCE_PRESETS = [
  { label: '15 jours', days: 15 },
  { label: '1 mois', months: 1 },
  { label: '2 mois', months: 2 },
];

function formatDateFrInput(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Due date (JJ/MM/AAAA) produced by a quick-choice preset, counted from today. */
function echeancePresetValue(preset: { days?: number; months?: number }): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  if (preset.months) {
    date.setMonth(date.getMonth() + preset.months);
  }
  if (preset.days) {
    date.setDate(date.getDate() + preset.days);
  }
  return formatDateFrInput(date);
}

const formatDh = (value: number) => `${value.toLocaleString('fr-FR')} DH`;

type AmountTone = 'default' | 'success' | 'danger';

/** Key-value amount row (label textSecondary, value textPrimary 600). */
function AmountRow({ label, value, tone = 'default' }: { label: string; value: string; tone?: AmountTone }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const color = tone === 'success' ? colors.success : tone === 'danger' ? colors.danger : colors.textPrimary;
  return (
    <View style={styles.amountRow}>
      <Text style={styles.amountLabel}>{label}</Text>
      <Text style={[styles.amountValue, { color }]}>{value}</Text>
    </View>
  );
}

/** Big figure in the hero card. */
function HeroFigure({ label, value, tone = 'default' }: { label: string; value: string; tone?: AmountTone }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const color = tone === 'success' ? colors.success : tone === 'danger' ? colors.danger : colors.textPrimary;
  return (
    <View style={styles.heroFigure}>
      <Text style={styles.heroFigureLabel}>{label}</Text>
      <Text style={[styles.heroFigureValue, { color }]} numberOfLines={1}>{value}</Text>
    </View>
  );
}

type NavType = StackNavigationProp<RootStackParamList, 'DetailCommande'>;

export default function DetailCommande() {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, width } = useLayout();
  const route = useRoute<RouteType>();
  const navigation = useNavigation<NavType>();
  const dispatch = useAppDispatch();
  const { selected: commande } = useAppSelector((s) => s.commandes);
  const canManageAdminFeatures = useAppSelector((s) => isAdminOrSuperAgentRole(s.auth.role));
  const { commandeId } = route.params;

  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [montant, setMontant] = useState('');
  const [mode, setMode] = useState<ModePaiement>('ESPECES');
  const [datePaiement, setDatePaiement] = useState(new Date().toISOString().slice(0, 10));
  const [savingP, setSavingP] = useState(false);
  const [changingStatut, setChangingStatut] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [showFactureDialog, setShowFactureDialog] = useState(false);
  const [dateEcheanceInput, setDateEcheanceInput] = useState('');
  const [numeroFactureInput, setNumeroFactureInput] = useState('');
  const isDateEcheanceValid = /^\d{2}\/\d{2}\/\d{4}$/.test(dateEcheanceInput);
  const isNumeroFactureValid = numeroFactureInput.trim().length > 0;

  const load = useCallback(() => { dispatch(fetchCommandeById(commandeId)); }, [commandeId]);
  useEffect(() => { load(); }, [load]);

  if (!commande || commande.id !== commandeId) {
    return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  }

  const commandeRef = `CM${String(commande.id).padStart(8, '0')}`;
  const estFacture = commande.statut === 'FACTURE';
  const estBonCommande = commande.statut === 'BON_COMMANDE';
  const estDocumentEncaissement = estFacture || estBonCommande;
  const documentRef = estFacture
    ? commande.numeroFacture?.trim() || `FAC${String(commande.id).padStart(8, '0')}`
    : estBonCommande
      ? commande.numeroBonCommande?.trim() || `BC${String(commande.id).padStart(8, '0')}`
      : commandeRef;
  const pdfFileName = `${documentRef.replace(/[<>:"\/\\|?*]/g, '_')}.pdf`;
  const totalVerse = commande.paiements.reduce((s, p) => s + Number(p.montant), 0);
  const estPaye = commande.statut === 'PAYEE' || (Number(commande.totalTTC) > 0 && totalVerse >= Number(commande.totalTTC));
  const resteAPayer = commande.statut === 'PAYEE' ? 0 : Math.max(0, Number(commande.totalTTC) - totalVerse);
  const statutTone = STATUT_TONES[commande.statut] ?? 'neutral';
  const statutLabel = STATUT_LABELS[commande.statut] ?? commande.statut;
  const todayIso = new Date().toISOString().slice(0, 10);
  const estEcheanceDepassee = estDocumentEncaissement
    && !!commande.dateEcheance
    && commande.dateEcheance < todayIso
    && resteAPayer > 0;
  const totalLabel = estBonCommande ? 'Total hors taxe' : 'Total TTC';
  const totalValue = Number(estBonCommande ? commande.totalHT : commande.totalTTC);
  const modalWidth = { width: Math.min(560, width - 32) };

  const handleStatutNext = async () => {
    const next: Record<StatutCommande, StatutCommande | null> = {
      DEVIS: 'FACTURE',
      PAYEE: null,
      FACTURE: null,
      BON_COMMANDE: null,
    };
    const nextStatut = next[commande.statut];
    if (!nextStatut) return;
    if (nextStatut === 'FACTURE') {
      setNumeroFactureInput(commande.numeroFacture?.trim() || `FAC${String(commande.id).padStart(8, '0')}`);
      setDateEcheanceInput('');
      setShowFactureDialog(true);
      return;
    }
    setChangingStatut(true);
    await dispatch(updateStatutCommande({ id: commandeId, statut: nextStatut }));
    setChangingStatut(false);
  };

  const handleConfirmerFacture = async () => {
    if (!isDateEcheanceValid || !isNumeroFactureValid) return;
    const [day, month, year] = dateEcheanceInput.split('/');
    const dateIso = `${year}-${month}-${day}`;
    setShowFactureDialog(false);
    setChangingStatut(true);
    await dispatch(updateStatutCommande({
      id: commandeId,
      statut: 'FACTURE',
      dateEcheance: dateIso,
      numeroFacture: numeroFactureInput.trim(),
    }));
    setChangingStatut(false);
  };

  const handleAjouterPaiement = async () => {
    const m = parseFloat(montant);
    if (!m || m <= 0) return;
    setSavingP(true);
    try {
      await paiementService.create({
        commandeId,
        montant: m,
        date: datePaiement,
        modePaiement: mode,
      });
      load();
      const phone = commande.client?.telephone;
      if (phone) {
        const nouveauReste = Math.max(0, resteAPayer - m);
        const message = encodeURIComponent(
          `Bonjour ${commande.client?.nomRaisonSociale},\n` +
          `Nous avons bien reçu votre paiement de ${m.toFixed(2)} DH.\n` +
          `Référence : ${documentRef}\n` +
          `Reste à payer : ${nouveauReste.toFixed(2)} DH.\nMerci.`
        );
        Linking.openURL(`https://wa.me/${phone.replace(/\D/g, '')}?text=${message}`);
      }
      setShowPaiementModal(false);
      setMontant('');
    } catch {
      Alert.alert('Erreur', 'Impossible d\'enregistrer le paiement.');
    } finally {
      setSavingP(false);
    }
  };

  const handleWhatsApp = () => {
    const phone = commande.client?.telephone;
    if (!phone) return;
    const msg = encodeURIComponent(
      `Bonjour ${commande.client.nomRaisonSociale},\n${documentRef} - ${statutLabel}\n` +
      `${estBonCommande ? 'Total hors taxe' : 'Total TTC'}: ${Number(commande.totalTTC).toLocaleString('fr-FR')} DH\n` +
      `Reste à payer: ${resteAPayer.toLocaleString('fr-FR')} DH`
    );
    Linking.openURL(`https://wa.me/${phone}?text=${msg}`);
  };

  const confirmAction = (message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) onConfirm();
      return;
    }

    Alert.alert('Confirmer', message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Supprimer', style: 'destructive', onPress: onConfirm },
    ]);
  };

  const handleDeletePaiement = (paiementId: number) => {
    confirmAction('Confirmer la suppression de ce paiement ?', async () => {
      try {
        await paiementService.delete(paiementId);
        load();
      } catch (e: any) {
        const msg = e?.response?.data?.erreur
          ?? e?.response?.data?.message
          ?? 'Impossible de supprimer le paiement.';
        Alert.alert('Erreur', msg);
      }
    });
  };

  const handleDeleteCommande = () => {
    const typeLabel = estFacture
      ? 'facture'
      : estBonCommande
        ? 'bon de commande'
        : commande.statut === 'PAYEE'
          ? 'commande payée'
          : 'devis';
    confirmAction(`Confirmer la suppression de cette ${typeLabel} ?`, async () => {
      try {
        await dispatch(deleteCommande(commandeId)).unwrap();
        navigation.goBack();
      } catch (e: any) {
        const msg = typeof e === 'string'
          ? e
          : 'Impossible de supprimer cette commande.';
        Alert.alert('Erreur', msg);
      }
    });
  };

  const handleApplyEcheancePreset = (preset: { days?: number; months?: number }) => {
    setDateEcheanceInput(echeancePresetValue(preset));
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    if (Platform.OS === 'web') {
      try {
        const webGlobal = globalThis as any;
        const urlApi = webGlobal.URL;
        const doc = webGlobal.document;
        const BlobCtor = webGlobal.Blob;
        if (!urlApi || !doc || !BlobCtor) {
          Alert.alert('Erreur', 'Téléchargement PDF non supporté sur cet environnement web.');
          return;
        }

        const resp = await api.get(`/api/pdf/${commandeId}`, { responseType: 'blob' });
        const url = urlApi.createObjectURL(new BlobCtor([resp.data], { type: 'application/pdf' }));
        const link = doc.createElement('a');
        link.href = url;
        link.setAttribute('download', pdfFileName);
        doc.body.appendChild(link);
        link.click();
        link.remove();
        urlApi.revokeObjectURL(url);
      } catch {
        Alert.alert('Erreur', 'Impossible de générer le PDF.');
      } finally {
        setDownloadingPdf(false);
      }
    } else {
      try {
        await pdfService.download(commandeId, pdfFileName);
        Alert.alert('Téléchargement terminé', `${pdfFileName} est disponible sur l’appareil.`);
      } catch {
        Alert.alert('Erreur', 'Impossible de télécharger le PDF.');
      } finally {
        setDownloadingPdf(false);
      }
    }
  };

  const canEdit = commande.statut === 'DEVIS' || estDocumentEncaissement;
  const canDelete = commande.statut === 'DEVIS'
    || commande.statut === 'PAYEE'
    || (estDocumentEncaissement && canManageAdminFeatures);
  const canMarkFacture = commande.client?.type !== 'PARTICULIER' && commande.statut === 'DEVIS';
  const hasPhone = !!commande.client?.telephone;

  const actionsCard = (
    <Card>
      <SectionHeader title="Actions" />
      <View style={styles.actions}>
        {canMarkFacture && (
          <Button
            mode="contained"
            icon="file-check-outline"
            onPress={handleStatutNext}
            loading={changingStatut}
            contentStyle={styles.actionContent}
          >
            Marquer Facturé
          </Button>
        )}
        <Button
          mode="outlined"
          icon="file-pdf-box"
          textColor={colors.textPrimary}
          onPress={handleDownloadPdf}
          loading={downloadingPdf}
          disabled={downloadingPdf}
          contentStyle={styles.actionContent}
        >
          Télécharger PDF
        </Button>
        {hasPhone && (
          <Button
            mode="outlined"
            icon="whatsapp"
            textColor={colors.textPrimary}
            onPress={handleWhatsApp}
            contentStyle={styles.actionContent}
          >
            Envoyer WhatsApp
          </Button>
        )}
        {canEdit && (
          <Button
            mode="outlined"
            icon="pencil-outline"
            textColor={colors.textPrimary}
            onPress={() => navigation.navigate('NouvelleCommande', { commandeId: commande.id, clientId: commande.client?.id })}
            contentStyle={styles.actionContent}
          >
            {estFacture ? 'Modifier la Facture' : estBonCommande ? 'Modifier le Bon de commande' : 'Modifier le Devis'}
          </Button>
        )}
        {canDelete && (
          <Button
            mode="outlined"
            icon="delete-outline"
            textColor={colors.danger}
            onPress={handleDeleteCommande}
            style={styles.dangerBtn}
            contentStyle={styles.actionContent}
          >
            {estFacture
              ? 'Supprimer la Facture'
              : estBonCommande
                ? 'Supprimer le Bon de commande'
              : commande.statut === 'PAYEE'
                ? 'Supprimer la Commande Payée'
                : 'Supprimer le Devis'}
          </Button>
        )}
      </View>
    </Card>
  );

  return (
    <Screen>
      {/* Hero */}
      <Card raised style={styles.hero}>
        <View style={[styles.heroTop, !isDesktop && styles.heroTopMobile]}>
          <View style={styles.heroTitles}>
            <Text style={[styles.heroTitle, !isDesktop && styles.heroTitleMobile]} selectable>{documentRef}</Text>
            <View style={styles.metaRow}>
              <Text style={styles.meta} selectable>{commandeRef}</Text>
              <Text style={styles.metaDot}>·</Text>
              <MaterialCommunityIcons name="calendar-blank-outline" size={14} color={colors.textSecondary} />
              <Text style={styles.meta}>{new Date(commande.date).toLocaleDateString('fr-FR')}</Text>
            </View>
            {estDocumentEncaissement && commande.dateEcheance ? (
              <View style={styles.metaRow}>
                <MaterialCommunityIcons
                  name="calendar-clock"
                  size={14}
                  color={estEcheanceDepassee ? colors.danger : colors.warning}
                />
                <Text style={[styles.echeance, { color: estEcheanceDepassee ? colors.danger : colors.warning }]}>
                  Échéance : {new Date(commande.dateEcheance).toLocaleDateString('fr-FR')}
                </Text>
              </View>
            ) : null}
          </View>
          <View style={[styles.heroBadges, !isDesktop && styles.heroBadgesMobile]}>
            <Badge label={statutLabel} tone={statutTone} />
            {estPaye && commande.statut !== 'PAYEE' ? <Badge label="Payé" tone="success" icon="check" /> : null}
            {estEcheanceDepassee && <Badge label="Echéance dépassée" tone="danger" icon="clock-alert-outline" />}
          </View>
        </View>

        <View style={styles.hairline} />

        <View style={[styles.heroBottom, isDesktop && styles.heroBottomDesktop]}>
          <View style={styles.clientBlock}>
            <Avatar name={commande.client?.nomRaisonSociale} size={48} />
            <View style={styles.clientTexts}>
              <Text style={styles.clientLabel}>Client</Text>
              <Text style={styles.clientName} numberOfLines={2}>{commande.client?.nomRaisonSociale ?? '—'}</Text>
              {hasPhone ? (
                <View style={styles.metaRow}>
                  <MaterialCommunityIcons name="phone-outline" size={14} color={colors.textSecondary} />
                  <Text style={styles.meta} selectable>{commande.client.telephone}</Text>
                </View>
              ) : null}
            </View>
          </View>
          <View style={[styles.heroFigures, !isDesktop && styles.heroFiguresMobile]}>
            <HeroFigure label={totalLabel} value={formatDh(totalValue)} />
            {estDocumentEncaissement && (
              <HeroFigure
                label="Reste à payer"
                value={formatDh(resteAPayer)}
                tone={resteAPayer > 0 ? 'danger' : 'success'}
              />
            )}
          </View>
        </View>
      </Card>

      <View style={[styles.body, isDesktop && styles.bodyDesktop]}>
        <View style={styles.mainCol}>
          {/* Lignes */}
          <Card>
            <SectionHeader
              title="Articles"
              subtitle={`${commande.lignesCommande.length} article${commande.lignesCommande.length > 1 ? 's' : ''}`}
            />
            {commande.lignesCommande.map((l, index) => (
              <View key={l.id} style={[styles.ligneRow, index > 0 && styles.rowDivider]}>
                <View style={styles.ligneIcon}>
                  <MaterialCommunityIcons name="package-variant-closed" size={20} color={colors.textPrimary} />
                </View>
                <View style={styles.ligneInfo}>
                  <Text style={styles.ligneName}>
                    {getServiceDisplayName(l.produitService?.code, l.produitService?.nom)}
                  </Text>
                  <Text style={styles.ligneCode}>{l.produitService?.code?.replace('*', '')} × {l.quantite}</Text>
                  {l.detail ? <Text style={styles.ligneDetail}>{l.detail}</Text> : null}
                </View>
              </View>
            ))}
            <View style={styles.totalsBlock}>
              {!estBonCommande && (
                <>
                  <AmountRow label="Total HT" value={formatDh(Number(commande.totalHT))} />
                  <AmountRow label="TVA" value={formatDh(Number(commande.tva))} />
                </>
              )}
              <View style={[styles.grandTotalRow, !estBonCommande && styles.grandTotalDivider]}>
                <Text style={styles.grandTotalLabel}>{totalLabel}</Text>
                <Text style={styles.grandTotalValue}>{formatDh(totalValue)}</Text>
              </View>
            </View>
          </Card>

          {/* Paiements des factures et des bons de commande */}
          {estDocumentEncaissement && (
            <Card>
              <SectionHeader
                title="Paiements"
                right={(
                  <Button
                    mode="outlined"
                    compact
                    icon="plus"
                    textColor={colors.textPrimary}
                    onPress={() => setShowPaiementModal(true)}
                  >
                    Ajouter
                  </Button>
                )}
              />
              {commande.paiements.length === 0 ? (
                <View style={styles.emptyBox}>
                  <MaterialCommunityIcons name="cash-remove" size={18} color={colors.textMuted} />
                  <Text style={styles.empty}>Aucun paiement enregistré</Text>
                </View>
              ) : (
                commande.paiements.map((p, index) => {
                  const modeOption = MODE_OPTIONS.find((o) => o.value === p.modePaiement);
                  return (
                    <View key={p.id} style={[styles.paiementRow, index > 0 && styles.rowDivider]}>
                      <View style={styles.paiementIcon}>
                        <MaterialCommunityIcons name={modeOption?.icon ?? 'cash'} size={20} color={colors.success} />
                      </View>
                      <View style={styles.ligneInfo}>
                        <Text style={styles.paiementMontant}>{formatDh(Number(p.montant))}</Text>
                        <Text style={styles.paiementDate}>
                          {new Date(p.date).toLocaleDateString('fr-FR')} · {modeOption?.label ?? p.modePaiement}
                        </Text>
                      </View>
                      {canManageAdminFeatures && (
                        <Button compact mode="text" textColor={colors.danger} onPress={() => handleDeletePaiement(p.id)}>
                          Supprimer
                        </Button>
                      )}
                    </View>
                  );
                })
              )}
              <View style={styles.totalsBlock}>
                <AmountRow label="Total versé" value={formatDh(totalVerse)} tone="success" />
                <View style={[styles.grandTotalRow, styles.grandTotalDivider]}>
                  <Text style={styles.grandTotalLabel}>Reste à payer</Text>
                  <Text style={[styles.grandTotalValue, { color: resteAPayer > 0 ? colors.danger : colors.success }]}>
                    {formatDh(resteAPayer)}
                  </Text>
                </View>
              </View>
            </Card>
          )}
        </View>

        <View style={isDesktop ? styles.sideColDesktop : undefined}>
          {actionsCard}
        </View>
      </View>

      {/* Conversion en Facture */}
      <Portal>
        <Modal
          visible={showFactureDialog}
          onDismiss={() => setShowFactureDialog(false)}
          contentContainerStyle={[styles.modal, modalWidth]}
        >
          <Text style={styles.modalTitle}>Convertir en Facture</Text>
          <Text style={styles.modalSubtitle}>{documentRef} · {commande.client?.nomRaisonSociale ?? '—'}</Text>
          <TextInput
            label="Numéro de facture *"
            value={numeroFactureInput}
            onChangeText={setNumeroFactureInput}
            autoCapitalize="characters"
            maxLength={100}
            style={styles.input}
            error={numeroFactureInput.length > 0 && !isNumeroFactureValid}
          />
          <TextInput
            label="Date d'échéance *"
            value={dateEcheanceInput}
            onChangeText={setDateEcheanceInput}
            placeholder="JJ/MM/AAAA"
            keyboardType="numeric"
            maxLength={10}
            style={styles.input}
            error={dateEcheanceInput.length > 0 && !isDateEcheanceValid}
          />
          {dateEcheanceInput.length > 0 && !isDateEcheanceValid && (
            <Text style={styles.fieldError}>Format invalide. Utilisez JJ/MM/AAAA</Text>
          )}
          <Text style={styles.fieldLabel}>Choix rapide</Text>
          <View style={styles.chipsRow}>
            {ECHEANCE_PRESETS.map((preset) => (
              <FilterChip
                key={preset.label}
                label={preset.label}
                icon="calendar-plus"
                selected={dateEcheanceInput === echeancePresetValue(preset)}
                onPress={() => handleApplyEcheancePreset(preset)}
              />
            ))}
          </View>
          <View style={styles.modalActions}>
            <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowFactureDialog(false)}>Annuler</Button>
            <Button
              mode="contained"
              onPress={handleConfirmerFacture}
              disabled={!isDateEcheanceValid || !isNumeroFactureValid || changingStatut}
              loading={changingStatut}
            >Confirmer</Button>
          </View>
        </Modal>
      </Portal>

      {/* Paiement Modal */}
      <Portal>
        <Modal
          visible={showPaiementModal}
          onDismiss={() => setShowPaiementModal(false)}
          contentContainerStyle={[styles.modal, modalWidth]}
        >
          <Text style={styles.modalTitle}>Ajouter un Paiement</Text>
          <Text style={styles.modalSubtitle}>Reste à payer : {formatDh(resteAPayer)}</Text>
          <TextInput
            label="Montant (DH) *"
            value={montant}
            onChangeText={setMontant}
            keyboardType="decimal-pad"
            style={styles.input}
          />
          <TextInput
            label="Date"
            value={datePaiement}
            onChangeText={setDatePaiement}
            style={styles.input}
            placeholder="AAAA-MM-JJ"
          />
          <Text style={styles.fieldLabel}>Mode de Paiement</Text>
          <View style={styles.chipsRow}>
            {MODE_OPTIONS.map((o) => (
              <FilterChip
                key={o.value}
                label={o.label}
                icon={o.icon}
                selected={mode === o.value}
                onPress={() => setMode(o.value)}
              />
            ))}
          </View>
          <View style={styles.modalActions}>
            <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowPaiementModal(false)}>Annuler</Button>
            <Button mode="contained" onPress={handleAjouterPaiement} loading={savingP} disabled={savingP || !montant}>
              Enregistrer
            </Button>
          </View>
        </Modal>
      </Portal>
    </Screen>
  );
}

const useStyles = makeStyles((c) => ({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: c.background },

  // Hero
  hero: { padding: 24, marginBottom: 24 },
  heroTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
  heroTopMobile: { flexDirection: 'column', gap: 12 },
  heroTitles: { flexShrink: 1, gap: 6 },
  heroTitle: { ...TYPOGRAPHY.display, color: c.textPrimary },
  heroTitleMobile: { fontSize: 24, lineHeight: 30 },
  heroBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' },
  heroBadgesMobile: { justifyContent: 'flex-start' },
  metaRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  meta: { fontSize: 14, color: c.textSecondary },
  metaDot: { fontSize: 14, color: c.textMuted },
  echeance: { fontSize: 14, fontWeight: '600' },
  hairline: { height: 1, backgroundColor: c.divider, marginVertical: 20 },
  heroBottom: { gap: 20 },
  heroBottomDesktop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  clientBlock: { flexDirection: 'row', alignItems: 'center', gap: 14, flexShrink: 1 },
  clientTexts: { flexShrink: 1, gap: 2 },
  clientLabel: { ...TYPOGRAPHY.overline, color: c.textSecondary, textTransform: 'uppercase' },
  clientName: { ...TYPOGRAPHY.heading, color: c.textPrimary },
  heroFigures: { flexDirection: 'row', gap: 32 },
  heroFiguresMobile: { gap: 24, flexWrap: 'wrap' },
  heroFigure: { gap: 2 },
  heroFigureLabel: { fontSize: 13, color: c.textSecondary },
  heroFigureValue: { fontSize: 24, lineHeight: 30, fontWeight: '800', letterSpacing: -0.3 },

  // Body layout
  body: { gap: 24 },
  bodyDesktop: { flexDirection: 'row', alignItems: 'flex-start' },
  mainCol: { flex: 1, minWidth: 0, gap: 24 },
  sideColDesktop: { width: 340 },

  // Lines
  ligneRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 14, paddingVertical: 16 },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  ligneIcon: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ligneInfo: { flex: 1, minWidth: 0 },
  ligneName: { fontSize: 15, fontWeight: '600', color: c.textPrimary },
  ligneCode: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  ligneDetail: { fontSize: 13, color: c.textSecondary, marginTop: 4 },

  // Totals
  totalsBlock: { borderTopWidth: 1, borderTopColor: c.divider, paddingTop: 12, marginTop: 4, gap: 8 },
  amountRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  amountLabel: { fontSize: 14, color: c.textSecondary },
  amountValue: { fontSize: 15, fontWeight: '600' },
  grandTotalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  grandTotalDivider: { borderTopWidth: 1, borderTopColor: c.divider, paddingTop: 12, marginTop: 4 },
  grandTotalLabel: { ...TYPOGRAPHY.subheading, color: c.textPrimary },
  grandTotalValue: { fontSize: 18, fontWeight: '800', color: c.textPrimary },

  // Payments
  paiementRow: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 16 },
  paiementIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: c.successSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paiementMontant: { fontSize: 15, fontWeight: '700', color: c.textPrimary },
  paiementDate: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  emptyBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    marginBottom: 12,
  },
  empty: { fontSize: 14, color: c.textSecondary },

  // Actions
  actions: { gap: 10 },
  actionContent: { minHeight: 44 },
  dangerBtn: { borderColor: c.danger },

  // Modals
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    alignSelf: 'center',
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary },
  modalSubtitle: { ...TYPOGRAPHY.body, color: c.textSecondary, marginTop: 2, marginBottom: 4 },
  input: { marginTop: 12 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textSecondary, marginTop: 16, marginBottom: 8 },
  fieldError: { color: c.danger, fontSize: 13, marginTop: 6 },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 24 },
}));
